import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import Footer from './components/coreLayout/Footer';
import Header from './components/coreLayout/Header';
import ScrollToTop from './components/coreLayout/ScrollToTop';
import Pages from './utils/PageRouter';
import SnackbarContainer from './utils/SnackbarContainer';
import { Product } from './utils/models/products';
import { BookingStoreProvider } from './utils/stores/BookingStore';
import { ProductsStoreProvider } from './utils/stores/ProductsStore';
import { StepperStoreProvider } from './utils/stores/bookingSections/StepperStore';
import { theme } from './utils/theme';

interface AppShellProps {
    /** Server-fetched products (prerender) used to seed the catalogue for SSR + hydration. */
    readonly initialProducts?: Product[];
}

/**
 * Router-agnostic application tree shared by the client (BrowserRouter) and the
 * server/prerender (StaticRouter) entries. Emotion's CacheProvider, the Unhead
 * provider and the Router itself are supplied by each entry so they can differ
 * between client and server.
 */
export default function AppShell({ initialProducts }: AppShellProps) {
    useEffect(() => {
        TagManager.initialize({ gtmId: 'GTM-T4CD2VT' });
    }, []);

    return (
        <ProductsStoreProvider initialProducts={initialProducts}>
            <StepperStoreProvider>
                <BookingStoreProvider>
                    <div className="App">
                        <ScrollToTop />
                        <SnackbarContainer />
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                                <Header />
                                <Box component="main" sx={{ flexGrow: 1 }}>
                                    <Pages />
                                </Box>
                                <Footer />
                            </Box>
                        </ThemeProvider>
                    </div>
                </BookingStoreProvider>
            </StepperStoreProvider>
        </ProductsStoreProvider>
    );
}
